/**
 * Toon look, shared. One gradient ramp, one material cache, one outline trick.
 *
 * Per the studio's rendering rules: cel materials over PBR (no env map, no
 * specular), per-face normals for the faceted plastic-toy read, and every
 * material shared so a room of 40 props costs 40 matrices, not 40 shaders.
 */

import * as THREE from 'three'

let gradient: THREE.DataTexture | null = null

/** Three-step ramp. Fewer steps = more plastic, which is what we want. */
export function toonGradient(): THREE.DataTexture {
  if (gradient) return gradient
  const steps = new Uint8Array([90, 168, 232, 255])
  const tex = new THREE.DataTexture(steps, steps.length, 1, THREE.RedFormat)
  tex.minFilter = THREE.NearestFilter
  tex.magFilter = THREE.NearestFilter
  tex.generateMipmaps = false
  tex.needsUpdate = true
  gradient = tex
  return tex
}

const cache = new Map<string, THREE.MeshToonMaterial>()

export function toonMaterial(color: number, opts: { emissive?: number } = {}): THREE.MeshToonMaterial {
  const key = `${color}:${opts.emissive ?? 0}`
  const hit = cache.get(key)
  if (hit) return hit
  const mat = new THREE.MeshToonMaterial({
    color,
    gradientMap: toonGradient(),
    emissive: opts.emissive ?? 0x000000,
  })
  cache.set(key, mat)
  return mat
}

let outlineMat: THREE.MeshBasicMaterial | null = null

/** Inverted-hull outline material. Cheap, and only worth it on hero objects. */
export function outlineMaterial(): THREE.MeshBasicMaterial {
  if (!outlineMat) {
    outlineMat = new THREE.MeshBasicMaterial({ color: 0x2b2118, side: THREE.BackSide })
  }
  return outlineMat
}

/**
 * Bake flat per-face normals. Faceted low-poly shading has to come from the
 * geometry, not from a per-fragment flag — see the studio rendering notes.
 */
export function facet(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = geometry.index ? geometry.toNonIndexed() : geometry
  if (flat !== geometry) geometry.dispose()
  flat.computeVertexNormals()
  return flat
}

export function disposeToonCaches(): void {
  for (const m of cache.values()) m.dispose()
  cache.clear()
  outlineMat?.dispose()
  outlineMat = null
  gradient?.dispose()
  gradient = null
}
